/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Bot, User, Copy, ThumbsUp, ThumbsDown, BarChart2, TrendingUp, AlertTriangle } from 'lucide-react';
import { ChatMessage } from '../../types/chat';
import { formatCurrency, formatCompact } from '../../lib/utils/format';
import { TradingChart } from '../TradingChart';

// --- Helper: Simple Markdown Parser ---
// Renders **text** as bold and - items as bullets.
const FormattedText: React.FC<{ text: string }> = ({ text }) => {
    // Split by newlines first
    const lines = text.split('\n');
    
    return (
        <div className="space-y-1">
            {lines.map((line, idx) => {
                // List Item Detection
                if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
                    const content = line.trim().substring(2);
                    return (
                        <div key={idx} className="flex gap-2 pl-2">
                            <span className="text-stone-400">•</span>
                            <span dangerouslySetInnerHTML={{ __html: parseBold(content) }}></span>
                        </div>
                    );
                }
                
                // Header Detection
                if (line.trim().startsWith('### ')) {
                    return <h4 key={idx} className="font-bold text-stone-800 pt-2">{line.trim().substring(4)}</h4>;
                }

                // Standard Line
                return (
                    <p key={idx} className={line.trim() === '' ? 'h-2' : ''} dangerouslySetInnerHTML={{ __html: parseBold(line) }}></p>
                );
            })}
        </div>
    );
};

const parseBold = (text: string) => {
    // Replace **text** with <strong>text</strong>
    // Note: Simple regex replacement. For full markdown, use a library.
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

// --- Token Data Widget ---
const ChatTokenWidget: React.FC<{ data: NonNullable<ChatMessage['data']> }> = ({ data }) => {
    const { token, price } = data;
    
    return (
        <div className="mt-3 mb-2 w-full max-w-sm bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="p-3 border-b border-stone-100 flex justify-between items-center bg-stone-50/50">
                <div className="flex items-center gap-2">
                    {token.image ? (
                        <img src={token.image} className="w-6 h-6 rounded-full" />
                    ) : (
                        <div className="w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center text-[10px] font-bold">{token.symbol[0]}</div>
                    )}
                    <span className="font-bold text-sm text-stone-900">{token.name}</span>
                    <span className="text-[10px] font-mono text-stone-500">${token.symbol}</span>
                </div>
                <span className="text-xs font-bold text-stone-800">{formatCurrency(price)}</span>
            </div>
            
            <div className="h-40 w-full relative">
                {/* Mini Chart Component reused */}
                <TradingChart 
                    symbol={token.symbol} 
                    basePrice={price} 
                    timeframe="1H" 
                    onTimeframeChange={() => {}} 
                    tokenMint={token.properties?.mintAddress}
                    launchDate={token.properties?.createdAt}
                />
                {/* Overlay to block interaction if needed, or allow it */}
            </div>

            <div className="grid grid-cols-3 gap-2 p-3 text-center border-t border-stone-100">
                <div>
                    <div className="text-[9px] text-stone-400 uppercase">Mkt Cap</div>
                    <div className="text-xs font-bold text-stone-700">${formatCompact(price * (token.properties?.supply || 1000000000))}</div>
                </div>
                <div>
                    <div className="text-[9px] text-stone-400 uppercase">Liquidity</div>
                    <div className="text-xs font-bold text-stone-700">$240K</div>
                </div>
                <div>
                    <div className="text-[9px] text-stone-400 uppercase">Risk</div>
                    <div className="text-xs font-bold text-green-600">Med</div>
                </div>
            </div>
        </div>
    );
};

interface ChatBubbleProps {
    message: ChatMessage;
    isTyping?: boolean;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({ message, isTyping }) => {
    const isUser = message.role === 'user';

    return (
        <div className={`flex gap-4 ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in group/row`}>
            {/* Avatar - Model */}
            {!isUser && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-stone-800 to-stone-900 flex items-center justify-center text-white shrink-0 shadow-sm mt-1">
                    <Bot size={16} />
                </div>
            )}

            <div className={`flex flex-col gap-1 max-w-[90%] md:max-w-[80%]`}>
                <div 
                    className={`
                        p-4 rounded-2xl text-sm leading-relaxed shadow-sm relative
                        ${isUser 
                            ? 'bg-white border border-stone-200 text-stone-800 rounded-tr-none' 
                            : 'bg-stone-50 border border-stone-200 text-stone-700 rounded-tl-none'
                        }
                    `}
                >
                    {message.data && (
                        <ChatTokenWidget data={message.data} />
                    )}

                    <div className="font-sans">
                        <FormattedText text={message.content} />
                        {isTyping && <span className="inline-block w-1.5 h-4 ml-1 bg-stone-400 animate-pulse align-middle"></span>}
                    </div>
                    
                    {!isUser && !isTyping && (
                        <div className="absolute -bottom-8 left-0 flex items-center gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity">
                            <button className="p-1.5 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-600 transition-colors" title="Copy">
                                <Copy size={12} />
                            </button>
                            <button className="p-1.5 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-600 transition-colors" title="Helpful">
                                <ThumbsUp size={12} />
                            </button>
                            <button className="p-1.5 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-600 transition-colors" title="Not Helpful">
                                <ThumbsDown size={12} />
                            </button>
                        </div>
                    )}
                </div>
                {isUser && (
                    <span className="text-[10px] text-stone-300 text-right pr-1">You</span>
                )}
            </div>

            {/* Avatar - User */}
            {isUser && (
                <div className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center text-stone-600 shrink-0 shadow-sm mt-1">
                    <User size={16} />
                </div>
            )}
        </div>
    );
};